<template>
  <div class="surat-keluar-detail-hc">
    <!-- Loading Overlay -->
    <div v-if="pageLoading" class="loading-overlay">
      <a-spin size="large" />
    </div>

    <template v-else-if="suratData && suratData.id">
      <!-- Page Header -->
      <div class="page-header">
        <h2 class="page-title">Detail Surat Keluar</h2>
        <p class="page-subtitle">{{ suratData.nomorSurat }}</p>
        <a-tag :style="getStatusStyle(suratData.status)" class="status-tag">
          <strong>{{
            suratData.status === 'Revisi' ? 'Revisi Verifikator' : suratData.status
          }}</strong>
        </a-tag>
      </div>

      <!-- Action Buttons -->
      <div class="action-buttons">
        <!-- Download Surat Button -->
        <a-button class="btn-pill-red-outline" @click="handleDownloadSurat" :disabled="!hasPdf">
          <template #icon>
            <DownloadOutlined />
          </template>
          DOWNLOAD SURAT
        </a-button>

        <!-- Ubah Surat Button (only for Verifikasi/Revisi) -->
        <a-button v-if="canEdit" type="default" class="btn-pill-black" @click="handleEditSurat">
          <template #icon>
            <EditOutlined />
          </template>
          UBAH SURAT
        </a-button>

        <!-- Batalkan Surat Button (only for owner) -->
        <a-button
          v-if="canCancel"
          type="default"
          class="btn-pill-black"
          @click="handleCancelSurat"
          :loading="cancelLoading"
        >
          <template #icon>
            <StopOutlined />
          </template>
          BATALKAN SURAT
        </a-button>

        <!-- Duplicate Button - Only show for owner sekretaris or super_admin, NOT for Upload File method -->
        <a-button
          v-if="canDuplicate"
          type="default"
          class="btn-pill-black"
          @click="handleDuplicateSurat"
        >
          <template #icon>
            <CopyOutlined />
          </template>
          BUAT SURAT BARU DARI SURAT INI
        </a-button>
      </div>

      <!-- Detail Revisi Section (only show for Revisi status) -->
      <DetailRevisi
        v-if="suratData.status === 'Revisi'"
        :verifikator="suratData.verifikator"
        :penandatangan="suratData.penandatangan"
        show-only="verifikator"
      />

      <!-- Detail Pembatalan Section (only show for Dibatalkan status) -->
      <DetailPembatalan
        :alasan-pembatalan="suratData.alasanPembatalan"
        :status="suratData.status"
      />

      <!-- Informasi Surat Card -->
      <div class="info-card">
        <h3 class="section-title">Informasi Surat</h3>

        <div class="info-row">
          <span class="info-label">Pengaju Surat</span>
          <span class="info-value">{{ formatCreator(suratData.creator) }}</span>
        </div>
        <div class="info-row">
          <span class="info-label">Waktu Dibuat/Unggah</span>
          <span class="info-value">{{ formatCreatedAt() }}</span>
        </div>
        <div class="info-row">
          <span class="info-label">Jenis Surat</span>
          <span class="info-value">{{ suratData.jenisSurat || '-' }}</span>
        </div>
        <div class="info-row">
          <span class="info-label">Nomor Surat</span>
          <span class="info-value">{{ suratData.nomorSurat || '-' }}</span>
        </div>
        <div class="info-row">
          <span class="info-label">Sifat Surat</span>
          <span class="info-value">{{ suratData.sifatSurat || '-' }}</span>
        </div>
        <div class="info-row">
          <span class="info-label">Bentuk Surat</span>
          <span class="info-value">{{ getBentukSuratDescription(suratData.bentukSurat) }}</span>
        </div>
        <div class="info-row">
          <span class="info-label">Penandatangan 1</span>
          <span class="info-value">{{ formatPenandatangan(suratData.penandatanganKiri) }}</span>
        </div>
        <div class="info-row">
          <span class="info-label">Penandatangan 2</span>
          <span class="info-value">{{ formatPenandatangan(suratData.penandatanganKanan) }}</span>
        </div>
        <div class="info-row">
          <span class="info-label">Verifikator</span>
          <span class="info-value">{{ formatVerifikatorList(suratData.verifikator) }}</span>
        </div>
        <div class="info-row" v-if="suratData.suratUndanganAcara">
          <span class="info-label">Surat Undangan Acara</span>
          <div class="surat-masuk-container">
            <PaperClipOutlined class="surat-masuk-icon" />

            <a href="#" @click.stop.prevent="openSuratMasukNewTab" class="surat-masuk-name">
              {{ getSuratMasukFileName(suratData.suratUndanganAcara.pathToFile) }}
            </a>

            <div class="surat-masuk-actions">
              <span
                @click.stop.prevent="openSuratMasukPreview"
                class="action-icon"
                title="Preview Surat"
              >
                <EyeOutlined />
              </span>
              <span
                @click.stop.prevent="openSuratMasukNewTab"
                class="action-icon"
                title="Buka di Tab Baru"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="14"
                  height="14"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                >
                  <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"></path>
                  <polyline points="15 3 21 3 21 9"></polyline>
                  <line x1="10" y1="14" x2="21" y2="3"></line>
                </svg>
              </span>
            </div>
          </div>
        </div>

        <template v-if="isSuratTugas && isArchivedStatus && hasNonPengurusWithBuktiAccess">
          <div
            v-for="perwakilan in nonPengurusPerwakilanList"
            :key="perwakilan.id"
            class="info-row"
            v-show="
              perwakilan.buktiPengiriman ||
                (perwakilanBuktiFiles[perwakilan.id] &&
                  perwakilanBuktiFiles[perwakilan.id].length > 0) ||
                suratData.status === 'Belum Dikirim'
            "
          >
            <span class="info-label">
              Bukti Pengiriman
              <div style="font-size: 12px; color: #bfbfbf; font-weight: 400; margin-top: 2px;">
                {{ perwakilan.namaNonPengurus }}
              </div>
            </span>

            <!-- Show existing bukti files if available -->
            <div
              v-if="perwakilan.buktiPengiriman"
              class="surat-masuk-container"
              style="flex-direction: column; align-items: flex-start; gap: 8px;"
            >
              <template
                v-for="(file, index) in getBuktiFileList(perwakilan.buktiPengiriman)"
                :key="index"
              >
                <div style="display: flex; align-items: center; width: 100%;">
                  <PaperClipOutlined class="surat-masuk-icon" />

                  <a :href="file.url" target="_blank" class="surat-masuk-name" :title="file.name">
                    {{ file.name }}
                  </a>

                  <div class="surat-masuk-actions">
                    <span
                      @click.stop.prevent="openBuktiPreviewFromList(perwakilan.buktiPengiriman, index)"
                      class="action-icon"
                      title="Preview Bukti"
                    >
                      <EyeOutlined />
                    </span>

                    <span
                      @click.stop.prevent="() => window.open(file.url, '_blank')"
                      class="action-icon"
                      title="Buka di Tab Baru"
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="14"
                        height="14"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        stroke-width="2"
                      >
                        <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"></path>
                        <polyline points="15 3 21 3 21 9"></polyline>
                        <line x1="10" y1="14" x2="21" y2="3"></line>
                      </svg>
                    </span>
                  </div>
                </div>
              </template>
            </div>

            <!-- Show locally stored bukti (not yet uploaded to backend) -->
            <div
              v-else-if="
                perwakilanBuktiFiles[perwakilan.id] &&
                  perwakilanBuktiFiles[perwakilan.id].length > 0
              "
              class="surat-masuk-container"
              style="flex-direction: column; align-items: flex-start; gap: 8px;"
            >
              <template v-for="(file, index) in perwakilanBuktiFiles[perwakilan.id]" :key="index">
                <div style="display: flex; align-items: center; width: 100%;">
                  <PaperClipOutlined class="surat-masuk-icon" />
                  <span class="surat-masuk-name" style="color: #fa8c16;">
                    {{ file.name || file.originFileObj?.name }} (Belum diunggah)
                  </span>
                  <a-tag color="orange" style="margin-left: 8px; font-size: 11px;">Pending</a-tag>
                </div>
              </template>
              <!-- Option to add more or edit -->
              <a-button
                v-if="canPerformActions"
                type="link"
                size="small"
                @click="openBuktiPengirimanModal(perwakilan.id)"
                style="padding: 0; height: auto;"
              >
                <EditOutlined /> Ubah Bukti
              </a-button>
            </div>

            <!-- Show placeholder if no bukti -->
            <div v-else class="surat-masuk-container">
              <span style="color: #8c8c8c; font-style: italic;">-</span>
            </div>
          </div>
        </template>

        <!-- Bukti Pengiriman for Surat Lainnya (surat-level, hard copy) -->
        <div
          class="info-row"
          v-if="
            !isSuratTugas &&
              isArchivedStatus &&
              (suratData.status === 'Terkirim' || suratData.status === 'Belum Dikirim')
          "
        >
          <span class="info-label">Bukti Pengiriman</span>

          <!-- Show existing bukti files from backend -->
          <div
            v-if="hasSuratLainnyaBukti"
            class="surat-masuk-container"
            style="flex-direction: column; align-items: flex-start; gap: 8px;"
          >
            <template v-for="(buktiFile, index) in suratLainnyaBuktiList" :key="index">
              <div style="display: flex; align-items: center; width: 100%;">
                <PaperClipOutlined class="surat-masuk-icon" />
                <a :href="getFileUrl(buktiFile)" target="_blank" class="surat-masuk-name">
                  {{ getFileNameFromPath(buktiFile) || `Bukti Pengiriman ${index + 1}` }}
                </a>
                <div class="surat-masuk-actions">
                  <span @click="openBuktiPreview(buktiFile)" class="action-icon">
                    <EyeOutlined />
                  </span>
                  <span @click="window.open(getFileUrl(buktiFile))" class="action-icon">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="14"
                      height="14"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      stroke-width="2"
                    >
                      <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"></path>
                      <polyline points="15 3 21 3 21 9"></polyline>
                      <line x1="10" y1="14" x2="21" y2="3"></line>
                    </svg>
                  </span>
                </div>
              </div>
            </template>
          </div>

          <!-- Show locally stored bukti (not yet uploaded to backend) -->
          <div
            v-else-if="suratLainnyaBuktiFilesLocal.length > 0"
            class="surat-masuk-container"
            style="flex-direction: column; align-items: flex-start; gap: 8px;"
          >
            <template v-for="(file, index) in suratLainnyaBuktiFilesLocal" :key="index">
              <div style="display: flex; align-items: center; width: 100%;">
                <PaperClipOutlined class="surat-masuk-icon" />
                <span class="surat-masuk-name" style="color: #fa8c16;">
                  {{ file.name || file.originFileObj?.name }} (Belum diunggah)
                </span>
                <a-tag color="orange" style="margin-left: 8px; font-size: 11px;">Pending</a-tag>
              </div>
            </template>
            <!-- Option to add more or edit -->
            <a-button
              v-if="canPerformActions"
              type="link"
              size="small"
              @click="triggerSuratLainnyaBuktiInput"
              style="padding: 0; height: auto;"
            >
              <EditOutlined /> Ubah Bukti
            </a-button>
          </div>

          <!-- Show placeholder if no bukti -->
          <div v-else class="surat-masuk-container">
            <span style="color: #8c8c8c; font-style: italic;">-</span>
          </div>
        </div>

        <!-- Dokumen Surat - Works for both Surat Tugas and Surat Lainnya -->
        <div class="info-row" v-if="hasDokumenSurat">
          <span class="info-label">Dokumen Surat</span>
          <div class="surat-masuk-container">
            <!-- For Surat Tugas: Show all surat tugas -->
            <template v-if="isSuratTugas">
              <a
                v-for="(st, idx) in suratTugasList"
                :key="st.id"
                :href="getFileUrl(st.pdfPath)"
                target="_blank"
                class="file-link"
              >
                <PaperClipOutlined /> Surat Tugas {{ suratData.nomorSurat
                }}{{ suratTugasList.length > 1 ? ` (${idx + 1})` : '' }}
              </a>

              <div class="surat-masuk-actions">
                <span
                  @click.stop.prevent="openSuratTugasPreview"
                  class="action-icon"
                  title="Preview Surat"
                >
                  <EyeOutlined />
                </span>
                <span
                  @click.stop.prevent="openSuratTugasNewTab"
                  class="action-icon"
                  title="Buka di Tab Baru"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="14"
                    height="14"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="2"
                  >
                    <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"></path>
                    <polyline points="15 3 21 3 21 9"></polyline>
                    <line x1="10" y1="14" x2="21" y2="3"></line>
                  </svg>
                </span>
              </div>
            </template>

            <!-- For Surat Lainnya: Show single document (archived replaces original) -->
            <template v-else>
              <a :href="getFileUrl(suratLainnyaDocumentPath)" target="_blank" class="file-link">
                <PaperClipOutlined /> Surat {{ suratData.nomorSurat }}
              </a>

              <div class="surat-masuk-actions">
                <span
                  @click.stop.prevent="openSuratLainnyaPreview"
                  class="action-icon"
                  title="Preview Surat"
                >
                  <EyeOutlined />
                </span>
                <span
                  @click.stop.prevent="openSuratLainnyaNewTab"
                  class="action-icon"
                  title="Buka di Tab Baru"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="14"
                    height="14"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="2"
                  >
                    <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"></path>
                    <polyline points="15 3 21 3 21 9"></polyline>
                    <line x1="10" y1="14" x2="21" y2="3"></line>
                  </svg>
                </span>
              </div>
            </template>
          </div>
        </div>
      </div>

      <!-- Pengarsipan Surat Keluar Section (only for Penandatanganan status) -->
      <div class="archive-section" v-if="suratData.status === 'Penandatanganan'">
        <h3 class="section-title">Pengarsipan Surat Keluar</h3>
        <p class="section-description" v-if="isSuratTugas">
          Unggah dokumen surat tugas yang telah ditandatangani untuk setiap perwakilan dinas
        </p>
        <p class="section-description" v-else>
          Unggah dokumen surat yang telah ditandatangani
        </p>

        <!-- For Surat Tugas: Multiple uploaders (one per perwakilan) -->
        <template v-if="isSuratTugas">
          <div
            v-for="perwakilan in suratData.perwakilanDinas"
            :key="perwakilan.id"
            class="perwakilan-upload-card"
          >
            <h4 class="perwakilan-title">
              Surat Tugas -
              {{
                perwakilan.isNonPengurus
                  ? perwakilan.namaNonPengurus
                  : perwakilan.user?.name || 'Perwakilan'
              }}
            </h4>
            <div class="form-field">
              <!-- Show already uploaded file if exists -->
              <div v-if="perwakilan.signedPdfPath" class="already-uploaded-notice">
                <CheckCircleOutlined class="check-icon" />
                <span>File sudah diunggah</span>
                <a :href="getFileUrl(perwakilan.signedPdfPath)" target="_blank" class="view-link">
                  Lihat File
                </a>
              </div>

              <!-- Custom Upload Area -->
              <div
                class="upload-area"
                @click="triggerFileInput(perwakilan.id)"
                @drop.prevent="handleFileDrop($event, perwakilan.id)"
                @dragover.prevent="handleDragOver"
                @dragleave.prevent="handleDragLeave"
                @dragenter.prevent
              >
                <input
                  :ref="el => setFileInputRef(perwakilan.id, el)"
                  type="file"
                  accept=".pdf,.png,.jpg,.jpeg"
                  style="display: none"
                  @change="handleFileChange($event, perwakilan.id)"
                />
                <div class="upload-icon">
                  <svg
                    width="48"
                    height="48"
                    viewBox="0 0 48 48"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                  >
                    <path
                      d="M24 8V32M24 8L16 16M24 8L32 16M40 32V40H8V32"
                      stroke="#CCCCCC"
                      stroke-width="2"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    />
                  </svg>
                </div>
                <p class="upload-text">Klik atau seret file ke sini untuk mengunggah</p>
                <p class="upload-hint">
                  Unggah file dalam format PDF, PNG, atau JPG/JPEG (maksimal 10MB)
                </p>
              </div>

              <!-- Uploaded File Display -->
              <div v-if="getPerwakilanFile(perwakilan.id)" class="uploaded-files-list">
                <div class="uploaded-file-item">
                  <PaperClipOutlined class="file-icon" />
                  <a
                    :href="getFileObjectUrl(getPerwakilanFile(perwakilan.id))"
                    target="_blank"
                    class="file-name-link"
                  >
                    {{ getPerwakilanFile(perwakilan.id).name }}
                  </a>
                  <a-button
                    type="text"
                    danger
                    size="small"
                    @click="removeFile(perwakilan.id)"
                    class="btn-remove-file"
                  >
                    <template #icon>
                      <CloseOutlined />
                    </template>
                  </a-button>
                </div>
              </div>
            </div>

            <!-- Status Pengiriman for Non-Pengurus only -->
            <div v-if="perwakilan.isNonPengurus" class="form-field" style="margin-top: 24px;">
              <label class="field-label"
                >Status Pengiriman Surat <span class="required">*</span></label
              >
              <a-radio-group
                v-model:value="perwakilanStatusPengiriman[perwakilan.id]"
                class="status-radio-group"
              >
                <a-radio value="Belum dikirim">Belum dikirim</a-radio>
                <a-radio value="Sudah dikirim">Sudah dikirim</a-radio>
              </a-radio-group>
            </div>

            <!-- Upload Bukti Pengiriman button for non-pengurus (shows when status is Sudah dikirim) -->
            <div
              v-if="
                perwakilan.isNonPengurus &&
                  perwakilanStatusPengiriman[perwakilan.id] === 'Sudah dikirim'
              "
              class="bukti-action-section"
            >
              <a-button class="btn-pill-red" @click="openBuktiPengirimanModal(perwakilan.id)">
                <PlusOutlined /> Tambah Foto
              </a-button>

              <p v-if="perwakilan.buktiPengiriman" class="bukti-uploaded-notice">
                <CheckCircleOutlined style="color: #52c41a; margin-right: 8px" />
                Bukti pengiriman sudah diunggah
              </p>
            </div>
          </div>
        </template>

        <!-- For Surat Lainnya: Single uploader -->
        <template v-else>
          <div class="form-field">
            <label class="field-label">Dokumen Surat <span class="required">*</span></label>
            <p class="field-hint">
              Mohon scan surat fisik yang sudah ditandatangani terlebih dahulu dengan scanner
            </p>

            <!-- Custom Upload Area -->
            <div
              class="upload-area"
              @click="triggerFileInput(null)"
              @drop.prevent="handleFileDrop($event, null)"
              @dragover.prevent="handleDragOver"
              @dragleave.prevent="handleDragLeave"
              @dragenter.prevent
            >
              <input
                :ref="el => setFileInputRef(null, el)"
                type="file"
                accept=".pdf,.png,.jpg,.jpeg"
                multiple
                style="display: none"
                @change="handleFileChange($event, null)"
              />
              <div class="upload-icon">
                <svg
                  width="48"
                  height="48"
                  viewBox="0 0 48 48"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                >
                  <path
                    d="M24 8V32M24 8L16 16M24 8L32 16M40 32V40H8V32"
                    stroke="#CCCCCC"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                </svg>
              </div>
              <p class="upload-text">Klik atau seret file ke sini untuk mengunggah</p>
              <p class="upload-hint">
                Unggah satu atau lebih file dalam format PDF, PNG, atau JPG/JPEG (maksimal 10MB per
                file)
              </p>
            </div>

            <!-- Uploaded File Display -->
            <div v-if="uploadFileList.length > 0" class="uploaded-files-list">
              <div
                v-for="(file, index) in uploadFileList"
                :key="file.uid || index"
                class="uploaded-file-item"
              >
                <PaperClipOutlined class="file-icon" />
                <a :href="getFileObjectUrl(file)" target="_blank" class="file-name-link">{{
                  file.name
                }}</a>
                <a-button
                  type="text"
                  danger
                  size="small"
                  @click="removeFile(null, index)"
                  class="btn-remove-file"
                >
                  <template #icon>
                    <CloseOutlined />
                  </template>
                </a-button>
              </div>
            </div>
          </div>
        </template>

        <!-- Status Pengiriman Surat (only for Surat Lainnya) -->
        <div v-if="!isSuratTugas" class="form-field">
          <label class="field-label">Status Pengiriman Surat <span class="required">*</span></label>
          <a-radio-group v-model:value="statusPengiriman" class="status-radio-group">
            <a-radio value="Belum dikirim">Belum dikirim</a-radio>
            <a-radio value="Sudah dikirim">Sudah dikirim</a-radio>
          </a-radio-group>
        </div>

        <!-- Bukti Pengiriman Upload (only for Surat Lainnya when "Sudah dikirim") -->
        <div
          v-if="!isSuratTugas && statusPengiriman === 'Sudah dikirim'"
          class="bukti-upload-section"
        >
          <label class="field-label">Bukti Pengiriman <span class="required">*</span></label>

          <div class="bukti-upload-area">
            <input
              type="file"
              ref="suratLainnyaBuktiInputRef"
              accept=".pdf,.jpg,.jpeg,.png"
              multiple
              style="display: none"
              @change="handleSuratLainnyaBuktiFileChange"
            />

            <a-button
              @click="$refs.suratLainnyaBuktiInputRef?.click()"
              class="btn-pill-red-outline"
            >
              <PlusOutlined /> Tambah Foto
            </a-button>

            <!-- Uploaded bukti files list -->
            <div v-if="suratLainnyaBuktiFiles.length > 0" class="uploaded-files-list">
              <div
                v-for="(file, index) in suratLainnyaBuktiFiles"
                :key="file.uid || index"
                class="uploaded-file-item"
              >
                <PaperClipOutlined class="file-icon" />
                <a :href="getFileObjectUrl(file)" target="_blank" class="file-name-link">{{
                  file.name
                }}</a>
                <a-button
                  type="text"
                  danger
                  size="small"
                  @click="removeSuratLainnyaBuktiFile(index)"
                  class="btn-remove-file"
                >
                  <template #icon>
                    <CloseOutlined />
                  </template>
                </a-button>
              </div>
            </div>
          </div>
        </div>

        <!-- Submit Button -->
        <a-button
          class="btn-pill-red"
          @click="arsipModalVisible = true"
          :loading="archiveLoading"
          :disabled="!canSubmitArchive"
          style="margin-top: 24px;"
        >
          SIMPAN ARSIP DAN UBAH STATUS
        </a-button>
      </div>

      <!-- Mark as Sent Button (only for owner with Belum Dikirim status) -->
      <div
        class="mark-sent-section"
        v-if="suratData.status === 'Belum Dikirim' && canPerformActions"
      >
        <a-button class="btn-pill-red" @click="openTandaiTerkirimModal" :loading="markSentLoading">
          TANDAI SURAT SEBAGAI TERKIRIM
        </a-button>
      </div>
    </template>

    <!-- Empty State -->
    <template v-else-if="!pageLoading">
      <div class="empty-state">
        <a-result
          status="404"
          title="Surat Tidak Ditemukan"
          sub-title="Surat keluar yang Anda cari tidak ditemukan atau telah dihapus."
        >
          <template #extra>
            <a-button type="primary" @click="router.push('/surat-keluar')">
              Kembali ke Daftar Surat
            </a-button>
          </template>
        </a-result>
      </div>
    </template>

    <!-- Cancel Modal -->
    <a-modal
      v-model:visible="cancelModalVisible"
      :footer="null"
      :mask-closable="false"
      centered
      class="cancel-modal"
    >
      <template #title>
        <strong>Yakin ingin menghentikan proses surat keluar?</strong>
      </template>
      <p class="modal-description">
        Tindakan ini akan membatalkan surat keluar dan proses verifikasi surat akan dihentikan.
        Surat yang sudah dibatalkan tidak dapat digunakan kembali.
      </p>
      <a-form layout="vertical">
        <a-form-item label="Alasan Pembatalan:" class="cancel-reason-field" required>
          <a-textarea
            v-model:value="cancelReason"
            placeholder="Masukkan alasan pembatalan surat"
            :rows="3"
          />
        </a-form-item>
      </a-form>
      <div class="modal-buttons">
        <a-button type="default" class="btn-pill-black" @click="cancelModalVisible = false">
          KEMBALI
        </a-button>
        <a-button class="btn-pill-red" :loading="cancelLoading" @click="confirmCancelSurat">
          YA, BATALKAN SURAT
        </a-button>
      </div>
    </a-modal>

    <!-- Archive Confirmation Modal -->
    <a-modal
      v-model:visible="arsipModalVisible"
      :footer="null"
      :mask-closable="false"
      centered
      class="confirm-modal"
    >
      <template #title>
        <strong>Yakin isi formulir sudah sesuai?</strong>
      </template>
      <p class="modal-description">
        Mohon cek kembali data surat sebelum menyimpan arsip.
        <strong>Data surat keluar yang sudah diarsipkan tidak dapat diubah kembali.</strong>
      </p>
      <div class="modal-buttons">
        <a-button type="default" class="btn-pill-black" @click="arsipModalVisible = false">
          KEMBALI
        </a-button>
        <a-button class="btn-pill-red" :loading="archiveLoading" @click="confirmSubmitArchive">
          YA, SIMPAN
        </a-button>
      </div>
    </a-modal>

    <!-- PDF Preview Modal (for both Surat Masuk and Surat Tugas) -->
    <PDFPreviewModal
      v-model:visible="suratMasukPreviewVisible"
      :title="previewTitle"
      :pdf-path="currentPdfPath"
      :pdf-list="pdfListForModal"
      :initial-index="currentSuratTugasIndex"
    />

    <!-- Proof of Delivery Modal (for non-pengurus) -->
    <a-modal
      v-model:visible="buktiPengirimanModalVisible"
      :footer="null"
      :mask-closable="false"
      centered
      width="600px"
      class="bukti-pengiriman-modal"
    >
      <template #title>
        <h3 style="margin: 0; font-size: 18px; font-weight: 600; color: #262626">
          Yakin tandai surat sebagai terkirim?
        </h3>
      </template>

      <p class="modal-description">
        Surat yang sudah berstatus "Terkirim" tidak dapat diubah statusnya menjadi "Dibatalkan"
      </p>

      <!-- Bukti Pengiriman Upload Section -->
      <div class="bukti-field">
        <label class="field-label"> Bukti Pengiriman <span class="required">*</span> </label>

        <!-- Add Photo Button -->
        <a-button type="default" class="btn-add-photo" @click="triggerBuktiFileInput">
          <template #icon>
            <PlusOutlined />
          </template>
          Tambah Foto
        </a-button>

        <!-- Hidden File Input -->
        <input
          ref="buktiFileInputRef"
          type="file"
          accept=".pdf,.png,.jpg,.jpeg"
          multiple
          style="display: none"
          @change="handleBuktiFileChange"
        />

        <!-- Uploaded Files List -->
        <div v-if="buktiPengirimanFiles.length > 0" class="bukti-files-list">
          <div
            v-for="(file, index) in buktiPengirimanFiles"
            :key="file.uid || index"
            class="bukti-file-item"
          >
            <!-- Preview thumbnail for images -->
            <div v-if="file.isExisting && file.url" class="file-preview-thumbnail">
              <img v-if="isImageFile(file.name)" :src="file.url" :alt="file.name" />
              <PaperClipOutlined v-else class="file-icon" />
            </div>
            <PaperClipOutlined v-else class="file-icon" />

            <a
              v-if="file.isExisting && file.url"
              :href="file.url"
              target="_blank"
              class="file-name-link"
            >
              {{ file.name }}
            </a>
            <span v-else class="file-name">{{ file.name }}</span>

            <div class="file-actions">
              <a-button
                v-if="!file.isExisting"
                type="text"
                size="small"
                class="btn-edit-file"
                @click="handleEditBuktiFile(index)"
              >
                <template #icon>
                  <EditOutlined />
                </template>
              </a-button>
              <a-button
                type="text"
                danger
                size="small"
                class="btn-remove-file"
                @click="removeBuktiFile(index)"
              >
                <template #icon>
                  <CloseOutlined />
                </template>
              </a-button>
            </div>
          </div>
        </div>
      </div>

      <!-- Modal Buttons -->
      <div class="modal-buttons">
        <a-button
          class="btn-pill-black"
          @click="closeBuktiPengirimanModal"
          :disabled="buktiPengirimanLoading"
        >
          KEMBALI
        </a-button>
        <a-button
          class="btn-pill-red"
          @click="handleSubmitBuktiPengiriman"
          :loading="buktiPengirimanLoading"
          :disabled="buktiPengirimanFiles.length === 0"
        >
          YA, SIMPAN
        </a-button>
      </div>
    </a-modal>

    <!-- Bukti Pengiriman Modal for Surat Lainnya (surat-level, hard copy) -->
    <a-modal
      v-model:visible="suratLainnyaBuktiModalVisible"
      :footer="null"
      :mask-closable="false"
      :width="600"
      centered
      class="bukti-pengiriman-modal"
    >
      <template #title>
        <div style="font-size: 16px; font-weight: 600; color: #262626;">
          Upload Bukti Pengiriman
        </div>
      </template>

      <p class="modal-description">
        Silakan upload bukti pengiriman surat (screenshot, foto, atau PDF).
      </p>

      <div class="upload-section">
        <a-upload
          v-model:file-list="suratLainnyaBuktiFiles"
          :before-upload="() => false"
          @change="handleSuratLainnyaBuktiChange"
          @remove="handleSuratLainnyaBuktiRemove"
          accept=".pdf,.jpg,.jpeg,.png"
          :multiple="true"
          :show-upload-list="false"
        >
          <a-button class="btn-pill-red-outline">
            <PlusOutlined />
            Pilih File
          </a-button>
        </a-upload>
        <div class="upload-hint">
          Format: PDF, JPG, PNG (Maks 10MB per file)
        </div>

        <div v-if="suratLainnyaBuktiFiles.length > 0" class="uploaded-files-list">
          <div
            v-for="(file, index) in suratLainnyaBuktiFiles"
            :key="file.uid || index"
            class="uploaded-file-item"
          >
            <PaperClipOutlined class="file-icon" />
            <a :href="getFileObjectUrl(file)" target="_blank" class="file-name-link">{{
              file.name
            }}</a>
            <a-button
              type="text"
              danger
              size="small"
              @click="removeSuratLainnyaBuktiFile(index)"
              class="btn-remove-file"
            >
              <template #icon>
                <CloseOutlined />
              </template>
            </a-button>
          </div>
        </div>
      </div>

      <div class="modal-buttons split-buttons">
        <a-button
          class="btn-pill-black full-width"
          @click="suratLainnyaBuktiModalVisible = false"
          :disabled="suratLainnyaBuktiUploading"
        >
          BATAL
        </a-button>
        <a-button
          class="btn-pill-red full-width"
          @click="handleSubmitSuratLainnyaBukti"
          :loading="suratLainnyaBuktiUploading"
          :disabled="suratLainnyaBuktiFiles.length === 0"
        >
          UPLOAD & TANDAI TERKIRIM
        </a-button>
      </div>
    </a-modal>
  </div>
</template>

<script>
import { defineComponent, ref, computed, onMounted } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { useStore } from 'vuex'
import { notification, Modal } from 'ant-design-vue'
import {
  DownloadOutlined,
  EditOutlined,
  StopOutlined,
  CopyOutlined,
  PaperClipOutlined,
  EyeOutlined,
  CloseOutlined,
  CheckCircleOutlined,
  PlusOutlined,
} from '@ant-design/icons-vue'
import { suratKeluarAPI, getFileUrl } from '@/services/axios/perki'
import { DetailRevisi, DetailPembatalan, PDFPreviewModal } from '@/components/SuratKeluar'
import moment from 'moment'
import 'moment/locale/id'

moment.locale('id')

export default defineComponent({
  name: 'SuratKeluarDetailHardCopy',
  components: {
    DownloadOutlined,
    EditOutlined,
    StopOutlined,
    CopyOutlined,
    PaperClipOutlined,
    EyeOutlined,
    CloseOutlined,
    CheckCircleOutlined,
    PlusOutlined,
    DetailRevisi,
    DetailPembatalan,
    PDFPreviewModal,
  },
  setup() {
    const router = useRouter()
    const route = useRoute()
    const store = useStore()

    const pageLoading = ref(true)
    const cancelLoading = ref(false)
    const archiveLoading = ref(false)
    const markSentLoading = ref(false)
    const suratData = ref({})
    const suratTugasList = ref([])

    // Get current user info for access control
    const currentUser = JSON.parse(localStorage.getItem('perki_user') || '{}')
    const currentUserId = currentUser.id
    const currentUserRole = localStorage.getItem('perki_role') || currentUser.role

    // Cancel modal
    const cancelModalVisible = ref(false)
    const cancelReason = ref('')

    // Archive confirmation modal
    const arsipModalVisible = ref(false)

    // PDF preview modal state
    const suratMasukPreviewVisible = ref(false)
    const suratMasukPreviewUrl = ref('')
    const previewTitle = ref('')
    const currentPdfPath = ref('')
    const currentSuratTugasIndex = ref(0)

    // Bukti Pengiriman Modal (for non-pengurus)
    const buktiPengirimanModalVisible = ref(false)
    const buktiPengirimanFiles = ref([])
    const buktiPengirimanLoading = ref(false)
    const buktiFileInputRef = ref(null)
    const currentNonPengurusId = ref(null) // Track which non-pengurus we're uploading for

    // Bukti Pengiriman for Surat Lainnya (surat-level, hard copy)
    const suratLainnyaBuktiModalVisible = ref(false)
    const suratLainnyaBuktiUploading = ref(false)
    const suratLainnyaBuktiFiles = ref([]) // Used both in archive section and modal (different contexts)
    const suratLainnyaBuktiInputRef = ref(null) // Ref for file input in archive section
    const suratLainnyaBuktiFilesLocal = ref([]) // For storing bukti files locally (Belum Dikirim status)
    const suratLainnyaBuktiInputInline = ref(null) // Ref for inline file input in info-card
    const buktiFileInputRefs = new Map() // Map<perwakilanId, HTMLInputElement> for per-perwakilan bukti inputs

    // Archive form (for Penandatanganan status)
    const fileInputRefs = new Map() // Map<perwakilanId, HTMLInputElement>
    const perwakilanFiles = ref({}) // Map of perwakilanId to file object
    const uploadFileList = ref([]) // For Surat Lainnya (non-Surat Tugas)
    const statusPengiriman = ref('Belum dikirim') // For Surat Lainnya only
    const perwakilanStatusPengiriman = ref({}) // Map<perwakilanId, 'Belum dikirim' | 'Sudah dikirim'>
    const perwakilanBuktiFiles = ref({}) // Map<perwakilanId, array of bukti files> - stored until archive submission

    // Helper to set file input refs
    const setFileInputRef = (perwakilanId, el) => {
      if (el) {
        const key = perwakilanId || 'default'
        fileInputRefs.set(key, el)
      }
    }

    // Helper to set bukti file input refs (for per-perwakilan bukti in Belum Dikirim)
    const setBuktiFileInputRef = (perwakilanId, el) => {
      if (el) {
        buktiFileInputRefs.set(perwakilanId, el)
      }
    }

    // Trigger file input for per-perwakilan bukti (Surat Tugas)
    const triggerPerwakilanBuktiInput = perwakilanId => {
      const input = buktiFileInputRefs.get(perwakilanId)
      if (input) {
        input.click()
      }
    }

    // Handle file change for per-perwakilan bukti (Surat Tugas)
    const handlePerwakilanBuktiFileChange = (event, perwakilanId) => {
      const files = Array.from(event.target.files || [])

      if (files.length === 0) return

      // Validate file size and type
      const validFiles = files.filter(file => {
        const isValidSize = file.size <= 10 * 1024 * 1024 // 10MB
        const isValidType = ['application/pdf', 'image/jpeg', 'image/jpg', 'image/png'].includes(
          file.type,
        )

        if (!isValidSize) {
          notification.warning({
            message: 'Ukuran file terlalu besar',
            description: `File ${file.name} melebihi 10MB`,
          })
        }
        if (!isValidType) {
          notification.warning({
            message: 'Format file tidak didukung',
            description: `File ${file.name} harus berformat PDF, JPG, atau PNG`,
          })
        }

        return isValidSize && isValidType
      })

      if (validFiles.length === 0) return

      // Convert to Ant Design file format
      const fileList = validFiles.map((file, index) => ({
        uid: `${Date.now()}-${index}`,
        name: file.name,
        status: 'done',
        originFileObj: file,
      }))

      // Store files for this perwakilan
      perwakilanBuktiFiles.value[perwakilanId] = fileList

      notification.success({
        message: 'File berhasil ditambahkan',
        description: `${validFiles.length} file siap diunggah saat menandai surat sebagai terkirim`,
      })

      // Reset input
      event.target.value = ''
    }

    // Trigger file input for Surat Lainnya bukti (surat-level)
    const triggerSuratLainnyaBuktiInput = () => {
      if (suratLainnyaBuktiInputInline.value) {
        suratLainnyaBuktiInputInline.value.click()
      }
    }

    // Handle file change for Surat Lainnya bukti (surat-level)
    const handleSuratLainnyaBuktiInlineChange = event => {
      const files = Array.from(event.target.files || [])

      if (files.length === 0) return

      // Validate file size and type
      const validFiles = files.filter(file => {
        const isValidSize = file.size <= 10 * 1024 * 1024 // 10MB
        const isValidType = ['application/pdf', 'image/jpeg', 'image/jpg', 'image/png'].includes(
          file.type,
        )

        if (!isValidSize) {
          notification.warning({
            message: 'Ukuran file terlalu besar',
            description: `File ${file.name} melebihi 10MB`,
          })
        }
        if (!isValidType) {
          notification.warning({
            message: 'Format file tidak didukung',
            description: `File ${file.name} harus berformat PDF, JPG, atau PNG`,
          })
        }

        return isValidSize && isValidType
      })

      if (validFiles.length === 0) return

      // Convert to Ant Design file format
      const fileList = validFiles.map((file, index) => ({
        uid: `${Date.now()}-${index}`,
        name: file.name,
        status: 'done',
        originFileObj: file,
      }))

      // Store files locally (replace existing)
      suratLainnyaBuktiFilesLocal.value = fileList

      notification.success({
        message: 'File berhasil ditambahkan',
        description: `${validFiles.length} file siap diunggah saat menandai surat sebagai terkirim`,
      })

      // Reset input
      event.target.value = ''
    }

    // Check if this is Surat Tugas type
    const isSuratTugas = computed(() => {
      return suratData.value.jenisSurat === 'Surat Tugas'
    })

    // Check if status is archived (Belum Dikirim or Terkirim)
    const isArchivedStatus = computed(() => {
      return ['Belum Dikirim', 'Terkirim'].includes(suratData.value.status)
    })

    // Check if has dokumen surat to display
    const hasDokumenSurat = computed(() => {
      if (isSuratTugas.value) {
        return suratTugasList.value.length > 0
      } else {
        // For Surat Lainnya: check if has document path (archived or original)
        return !!suratLainnyaDocumentPath.value
      }
    })

    // Get document path for Surat Lainnya (archived PDF if exists, otherwise original pdfPath)
    const suratLainnyaDocumentPath = computed(() => {
      if (isSuratTugas.value) return ''

      // For "Upload File" method, always use archivedPdfPaths if available
      // This applies to all statuses after creation (Verifikasi, Penandatanganan, Belum Dikirim, Terkirim)
      const isUploadFileMethod = suratData.value.metodePembuatan === 'Upload File'
      if (isUploadFileMethod && suratData.value.archivedPdfPaths) {
        try {
          const paths = JSON.parse(suratData.value.archivedPdfPaths)
          if (Array.isArray(paths) && paths.length > 0) {
            // Return LAST archived PDF (the latest signed/uploaded file)
            // Files are appended, so last file is the most recent
            return paths[paths.length - 1]
          }
        } catch (e) {
          console.error('Failed to parse archivedPdfPaths:', e)
        }
      }

      // For hard copy flow: after sent, prioritize archived PDF
      const isAlreadySent = ['Belum Dikirim', 'Terkirim'].includes(suratData.value.status)
      if (isAlreadySent && suratData.value.archivedPdfPaths) {
        try {
          const paths = JSON.parse(suratData.value.archivedPdfPaths)
          if (Array.isArray(paths) && paths.length > 0) {
            return paths[paths.length - 1]
          }
        } catch (e) {
          console.error('Failed to parse archivedPdfPaths:', e)
        }
      }

      // Otherwise use pdfPath
      return suratData.value.pdfPath || ''
    })

    // Get non-pengurus perwakilan list
    const nonPengurusPerwakilanList = computed(() => {
      if (!suratData.value.perwakilanDinas) return []
      return suratData.value.perwakilanDinas.filter(p => p.isNonPengurus)
    })

    // Check if there are non-pengurus with bukti access
    const hasNonPengurusWithBuktiAccess = computed(() => {
      return nonPengurusPerwakilanList.value.length > 0
    })

    // Check if Surat Lainnya has bukti pengiriman (surat-level)
    const hasSuratLainnyaBukti = computed(() => {
      return suratLainnyaBuktiList.value.length > 0
    })

    // Get Surat Lainnya bukti list (surat-level)
    const suratLainnyaBuktiList = computed(() => {
      if (!suratData.value.buktiPengiriman) return []

      // Parse buktiPengiriman - it's a JSON string from backend
      let buktiArray = []
      if (typeof suratData.value.buktiPengiriman === 'string') {
        try {
          buktiArray = JSON.parse(suratData.value.buktiPengiriman)
        } catch (e) {
          console.error('Failed to parse surat buktiPengiriman:', e)
          buktiArray = []
        }
      } else if (Array.isArray(suratData.value.buktiPengiriman)) {
        buktiArray = suratData.value.buktiPengiriman
      }

      return buktiArray
    })

    // PDF list for modal (for Surat Tugas navigation)
    const pdfListForModal = computed(() => {
      if (suratTugasList.value.length > 1) {
        return suratTugasList.value.map((surat, index) => ({
          path: surat.pdfPath,
          name: getSuratTitle(surat, index),
        }))
      }
      return []
    })

    // Get surat title for display
    const getSuratTitle = (surat, index) => {
      const perwakilanName = surat.perwakilan?.isNonPengurus
        ? surat.perwakilan.namaNonPengurus
        : surat.perwakilan?.user?.name
      return `Surat Tugas ${suratData.value.nomorSurat}${
        perwakilanName ? ` - ${perwakilanName}` : ` (${index + 1})`
      }`
    }

    // Get appropriate section title based on status
    const getSuratSectionTitle = () => {
      if (isArchivedStatus.value) {
        // For archived statuses, show as final document
        return isSuratTugas.value ? 'Surat Tugas' : 'Dokumen Surat'
      }
      // For non-archived, show as draft
      return isSuratTugas.value ? 'Draft Surat Tugas' : 'Dokumen Surat'
    }

    // Get status badge class for non-pengurus
    const getStatusBadgeClass = perwakilan => {
      const status = perwakilan.statusPengiriman || 'Belum dikirim'
      return {
        'status-terkirim': status === 'Sudah dikirim',
        'status-belum': status === 'Belum dikirim',
      }
    }

    // Add this helper function in setup()
    const getBuktiFileList = jsonString => {
      if (!jsonString) return []
      let paths = []
      try {
        // Try to parse if it's a JSON string array
        paths = JSON.parse(jsonString)
        // Handle case where JSON.parse returns a single string instead of array
        if (!Array.isArray(paths)) paths = [paths]
      } catch (e) {
        // If not JSON, treat as single path string
        paths = [jsonString]
      }

      return paths.map(path => ({
        name: getFileNameFromPath(path), // Uses your existing helper to get clean name
        url: getFileUrl(path), // Uses your existing helper to get full URL
      }))
    }

    // Check if current user is the owner (pengaju surat)
    const isOwner = computed(() => {
      return suratData.value.creator?.id === currentUserId
    })

    // Check if PDF is available
    const hasPdf = computed(() => {
      const status = suratData.value.status
      const isAlreadySent = ['Belum Dikirim', 'Terkirim'].includes(status)

      if (isSuratTugas.value) {
        // For Surat Tugas: check signedPdfPath if already sent, otherwise check pdfPath
        if (isAlreadySent) {
          return (
            suratTugasList.value.length > 0 &&
            suratTugasList.value.some(s => s.perwakilan?.signedPdfPath)
          )
        }
        return suratTugasList.value.length > 0 && suratTugasList.value.some(s => s.pdfPath)
      }

      // For Surat Lainnya: check signedPdfPath if already sent, otherwise check pdfPath
      if (isAlreadySent) {
        // For hard copy, signedPdfPath is in the first item of uploadFileList or perwakilanFiles
        // Actually for Surat Lainnya hard copy, it's stored differently
        // Check if any perwakilan has signedPdfPath (for hard copy flow)
        if (suratData.value.perwakilanDinas && suratData.value.perwakilanDinas.length > 0) {
          return suratData.value.perwakilanDinas.some(p => p.signedPdfPath)
        }
      }
      return !!suratData.value.pdfPath
    })

    // Check if surat uses "Upload File" method
    const isUploadFileMethod = computed(() => {
      return suratData.value?.metodePembuatan === 'Upload File'
    })

    // Check if user can perform actions (only sekretaris who owns it, or super_admin)
    const canPerformActions = computed(() => {
      // Only super_admin has full access
      if (currentUserRole === 'super_admin') {
        return true
      }
      // Sekretaris can only perform actions on their own surat
      return currentUserRole === 'sekretaris' && isOwner.value
    })

    // Check if can edit (only for owner sekretaris with Verifikasi/Revisi/Penandatanganan status, or super_admin)
    const canEdit = computed(() => {
      return (
        ['Verifikasi', 'Revisi', 'Penandatanganan'].includes(suratData.value.status) &&
        canPerformActions.value
      )
    })

    // Check if can cancel (only for owner sekretaris, or super_admin)
    // For Upload File method: hide when status is Terkirim
    const canCancel = computed(() => {
      if (!canPerformActions.value) return false
      // Hide cancel button for Upload File method when status is Terkirim
      if (isUploadFileMethod.value && suratData.value.status === 'Terkirim') return false
      return true
    })

    // Check if can duplicate - NOT allowed for Upload File method
    const canDuplicate = computed(() => {
      if (!canPerformActions.value) return false
      // Hide duplicate button for Upload File method
      if (isUploadFileMethod.value) return false
      return true
    })

    // Can mark as sent - only owner sekretaris can mark as sent, and only for Belum Dikirim status
    // For Surat Tugas: also require all non-pengurus to have bukti uploaded
    const canMarkSent = computed(() => {
      if (suratData.value.status !== 'Belum Dikirim' || !canPerformActions.value) {
        return false
      }

      // For Surat Tugas: check if all non-pengurus have bukti (either already uploaded or stored locally)
      if (isSuratTugas.value) {
        const nonPengurusList = nonPengurusPerwakilanList.value

        if (nonPengurusList.length === 0) {
          // No non-pengurus, can mark as sent
          return true
        }

        // All non-pengurus must have bukti either:
        // 1. Already uploaded (perwakilan.buktiPengiriman exists)
        // 2. Or stored locally (perwakilanBuktiFiles.value[perwakilan.id] has files)
        return nonPengurusList.every(p => {
          const hasUploadedBukti = p.buktiPengiriman && p.buktiPengiriman.length > 0
          const hasLocalBukti =
            perwakilanBuktiFiles.value[p.id] && perwakilanBuktiFiles.value[p.id].length > 0
          return hasUploadedBukti || hasLocalBukti
        })
      }

      // For non-Surat Tugas
      return true
    })

    // Check if can submit archive (only for owner sekretaris with Penandatanganan status, or super_admin)
    const canSubmitArchive = computed(() => {
      if (!canPerformActions.value) return false

      if (isSuratTugas.value) {
        // For Surat Tugas: ALL perwakilan must have files uploaded
        const totalPerwakilan = suratData.value.perwakilanDinas?.length || 0
        const uploadedCount = Object.keys(perwakilanFiles.value).length
        return uploadedCount === totalPerwakilan && totalPerwakilan > 0
      } else {
        // For Surat Lainnya: Check statusPengiriman and at least one file
        return statusPengiriman.value && uploadFileList.value.length > 0
      }
    })

    // Get status style (color, background, border). Border color == base color.
    const getStatusStyle = status => {
      const s = String(status || '')
      const palette = {
        Draft: { base: '#FAAE13', bg: '#FFFBE6' },
        Terkirim: { base: '#51A622', bg: '#F6FFED' },
        Dibatalkan: { base: '#D9D9D9', bg: '#FAFAFA' },
        Verifikasi: { base: '#0957D9', bg: '#E5F4FF' },
        Penandatanganan: { base: '#0B979D', bg: '#E5FFFB' },
        Revisi: { base: '#541CAB', bg: '#FAF1FF' },
        'Revisi Penandatangan': { base: '#C41E7F', bg: '#FFF0F6' },
        'Belum Dikirim': { base: '#D57117', bg: '#FFF7E5' },
      }
      const c = palette[s] || { base: '#d9d9d9', bg: '#f5f5f5' }
      return {
        color: c.base,
        backgroundColor: c.bg,
        border: `1px solid ${c.base}`,
      }
    }

    // Format creator name with role
    const formatCreator = creator => {
      if (!creator) return '-'
      const name = creator.name || 'Unknown'
      const jabatan = creator.jabatan || 'Staf Sekretariat'
      return `${name} (${jabatan})`
    }

    // Format created at with last updated info
    const formatCreatedAt = () => {
      const createdAt = suratData.value.createdAt
      const updatedAt = suratData.value.updatedAt

      if (!createdAt) return '-'

      let result = moment(createdAt).format('dddd YYYY-MM-DD HH:mm')

      // Check if updated at is different from created at (has been edited)
      if (updatedAt && moment(updatedAt).diff(moment(createdAt), 'seconds') > 1) {
        result += ` (Terakhir diperbarui ${moment(updatedAt).format('dddd YYYY-MM-DD HH:mm')})`
      }

      return result
    }

    // Get bentuk surat description
    const getBentukSuratDescription = bentuk => {
      if (bentuk === 'Hard copy') {
        return 'Hard copy (Menggunakan tanda tangan dan stempel basah. Surat dapat diambil di Sekretariat PERKI.)'
      }
      if (bentuk === 'Soft copy') {
        return 'Soft copy (Menggunakan tanda tangan dan stempel digital. Surat dapat diunduh di website ini (format PDF).)'
      }
      return bentuk || '-'
    }

    // Format penandatangan
    const formatPenandatangan = penandatangan => {
      if (!penandatangan) return 'Tidak ada'
      const name = penandatangan.name || 'Unknown'
      const gelar = [penandatangan.gelarDepan, penandatangan.gelarBelakang]
        .filter(Boolean)
        .join(', ')
      const jabatan = penandatangan.jabatan || ''
      let displayName = name
      if (gelar) displayName += `, ${gelar}`
      if (jabatan) displayName += ` (${jabatan})`
      return displayName
    }

    // Format verifikator list
    const formatVerifikatorList = verifikator => {
      if (!verifikator || verifikator.length === 0) return '-'

      // Sort by urutan first (ascending)
      const sortedVerifikator = [...verifikator].sort((a, b) => {
        const urutanA = a.urutan || 0
        const urutanB = b.urutan || 0
        return urutanA - urutanB
      })

      return sortedVerifikator
        .map((v, index) => {
          const name = v.user?.name || 'Unknown'
          let statusText = ''

          if (v.status === 'approved') {
            statusText = '(Setuju)'
          } else if (v.status === 'revised' || v.status === 'revision_requested') {
            statusText = '(Revisi)'
          } else if (v.status === 'pending') {
            // Check if all previous verifikators are approved
            const allPreviousApproved = sortedVerifikator
              .slice(0, index)
              .every(prev => prev.status === 'approved')

            if (allPreviousApproved) {
              statusText = '(Sedang Verifikasi)'
            } else {
              statusText = '(Menunggu)'
            }
          }
          return `${name} ${statusText}`
        })
        .join(' → ')
    }

    // Fetch surat detail
    const fetchData = async () => {
      pageLoading.value = true
      try {
        const id = route.params.id
        const response = await suratKeluarAPI.getById(id)

        if (response.data?.data) {
          suratData.value = response.data.data

          // Access control: only sekretaris who owns the surat can edit, or super_admin
          const isOwnerCheck = suratData.value.creator?.id === currentUserId
          const canEditCheck =
            currentUserRole === 'super_admin' || (currentUserRole === 'sekretaris' && isOwnerCheck)

          // If user cannot edit, redirect to view page
          if (!canEditCheck) {
            notification.warning({
              message: 'Anda hanya dapat melihat surat ini karena bukan pengaju surat',
            })
            router.replace(`/surat-keluar/${id}/view`)
            return
          }

          // Build surat tugas list from perwakilan dinas
          if (suratData.value.perwakilanDinas && suratData.value.perwakilanDinas.length > 0) {
            const status = suratData.value.status
            const isAlreadySent = ['Belum Dikirim', 'Terkirim'].includes(status)

            // Initialize per-perwakilan status pengiriman
            suratData.value.perwakilanDinas.forEach(p => {
              // For pengurus, always set to 'Sudah dikirim'
              // For non-pengurus, default to 'Belum dikirim'
              perwakilanStatusPengiriman.value[p.id] = p.isNonPengurus
                ? 'Belum dikirim'
                : 'Sudah dikirim'
            })

            suratTugasList.value = suratData.value.perwakilanDinas
              .filter(p => {
                // If already sent, check for signedPdfPath; otherwise check pdfPath
                return isAlreadySent ? p.signedPdfPath : p.pdfPath
              })
              .map(p => ({
                id: p.id,
                pdfPath: isAlreadySent ? p.signedPdfPath : p.pdfPath, // Use signed PDF if already sent
                perwakilan: p,
                fileName: `Surat Tugas ${suratData.value.nomorSurat}`,
              }))
          }
        }
      } catch (error) {
        console.error('Error fetching surat detail:', error)
        notification.error({ message: 'Gagal memuat data surat' })
      } finally {
        pageLoading.value = false
      }
    }

    // Handle download surat
    const handleDownloadSurat = () => {
      const status = suratData.value.status
      const isAlreadySent = ['Belum Dikirim', 'Terkirim'].includes(status)

      if (isSuratTugas.value) {
        // For Surat Tugas, download all PDFs
        // Use signedPdfPath if already sent (Belum Dikirim/Terkirim), otherwise use pdfPath
        suratTugasList.value.forEach((surat, index) => {
          setTimeout(() => {
            const pdfPath =
              isAlreadySent && surat.perwakilan?.signedPdfPath
                ? surat.perwakilan.signedPdfPath
                : surat.pdfPath
            const fileName = surat.fileName || `Surat_Tugas_${index + 1}.pdf`
            downloadFile(pdfPath, fileName)
          }, index * 100)
        })
      } else {
        // For Surat Lainnya, use suratLainnyaDocumentPath which handles all the logic
        const filePath = suratLainnyaDocumentPath.value

        if (filePath) {
          // Extract file extension from the path
          const extension = filePath.split('.').pop().toLowerCase()
          const fileName = `${suratData.value.nomorSurat || 'Surat'}.${extension}`
          downloadFile(filePath, fileName)
        } else {
          notification.warning({ message: 'Dokumen surat tidak tersedia' })
        }
      }
    }

    // Helper function to download file
    // Helper function to download file using Blob method
    const downloadFile = async (filePath, fileName) => {
      const fileUrl = getFileUrl(filePath)

      try {
        // 1. Fetch the file data
        const response = await fetch(fileUrl)

        // 2. Check if the fetch was successful
        if (!response.ok) throw new Error('Network response was not ok')

        // 3. Convert to Blob (Binary Large Object)
        const blob = await response.blob()

        // 4. Create a temporary URL for the Blob
        const blobUrl = window.URL.createObjectURL(blob)

        // 5. Create link and trigger download
        const link = document.createElement('a')
        link.href = blobUrl
        link.download = fileName || 'download.pdf' // This will now strictly work
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)

        // 6. Clean up the URL object to free memory
        window.URL.revokeObjectURL(blobUrl)
      } catch (error) {
        console.error('Download failed, falling back to new tab:', error)
        // Fallback: If fetch fails (e.g. strict CORS), open in new tab
        window.open(fileUrl, '_blank')
      }
    }

    // Handle edit surat - redirect to edit form
    const handleEditSurat = () => {
      const id = route.params.id
      router.push(`/surat-keluar/${id}`)
    }

    // Handle cancel surat
    const handleCancelSurat = () => {
      cancelReason.value = ''
      cancelModalVisible.value = true
    }

    // Confirm cancel surat
    const confirmCancelSurat = async () => {
      // Validate cancel reason
      if (!cancelReason.value?.trim()) {
        notification.warning({
          message: 'Alasan pembatalan harus diisi',
          description: 'Mohon isi alasan pembatalan surat sebelum melanjutkan.',
        })
        return
      }

      cancelLoading.value = true
      try {
        const id = route.params.id
        const result = await store.dispatch('suratKeluar/cancelSuratKeluar', {
          id: id,
          alasan: cancelReason.value || null,
        })

        if (result.status === 'success') {
          cancelModalVisible.value = false
          notification.success({ message: 'Surat keluar berhasil dibatalkan.' })
          router.push('/surat-keluar')
        } else {
          notification.error({ message: result.message || 'Gagal membatalkan surat' })
        }
      } catch (error) {
        console.error('Error canceling surat:', error)
        notification.error({ message: error.response?.data?.message || 'Gagal membatalkan surat' })
      } finally {
        cancelLoading.value = false
      }
    }

    // Handle duplicate surat
    const handleDuplicateSurat = () => {
      const id = route.params.id
      router.push('/surat-keluar/create?duplicateFrom=' + id)
    }

    // Open Tandai Terkirim modal based on surat type
    const openTandaiTerkirimModal = () => {
      if (isSuratTugas.value && hasNonPengurusWithBuktiAccess.value) {
        // For Surat Tugas with non-pengurus: open bukti pengiriman modal
        buktiPengirimanModalVisible.value = true
      } else if (!isSuratTugas.value) {
        // For Surat Lainnya: open surat lainnya bukti modal
        suratLainnyaBuktiModalVisible.value = true
      } else {
        // Direct mark as sent without bukti (shouldn't happen but fallback)
        handleMarkAsSent()
      }
    }

    // Handle mark as sent
    const handleMarkAsSent = async () => {
      markSentLoading.value = true
      try {
        const id = route.params.id

        // Upload bukti files first (both Surat Tugas per-perwakilan and Surat Lainnya surat-level)
        if (isSuratTugas.value) {
          // For Surat Tugas: Upload bukti for each non-pengurus that has bukti files stored
          const nonPengurusWithBukti = nonPengurusPerwakilanList.value.filter(
            p => perwakilanBuktiFiles.value[p.id] && perwakilanBuktiFiles.value[p.id].length > 0,
          )

          if (nonPengurusWithBukti.length > 0) {
            for (const perwakilan of nonPengurusWithBukti) {
              try {
                const buktiFormData = new FormData()
                const buktiFiles = perwakilanBuktiFiles.value[perwakilan.id]

                buktiFiles.forEach(file => {
                  buktiFormData.append('bukti_pengiriman', file.originFileObj || file)
                })

                await suratKeluarAPI.uploadBuktiPengiriman(id, perwakilan.id, buktiFormData)
              } catch (buktiError) {
                console.error(`Failed to upload bukti for perwakilan ${perwakilan.id}:`, buktiError)
                notification.warning({
                  message: 'Sebagian bukti gagal diunggah',
                  description: `Bukti untuk ${perwakilan.namaNonPengurus} gagal diunggah`,
                })
              }
            }
          }
        } else if (suratLainnyaBuktiFilesLocal.value.length > 0) {
          // For Surat Lainnya: Upload surat-level bukti files
          try {
            const buktiFormData = new FormData()
            suratLainnyaBuktiFilesLocal.value.forEach(file => {
              buktiFormData.append('files', file.originFileObj || file)
            })

            await suratKeluarAPI.uploadBuktiPengirimanSurat(id, buktiFormData)
          } catch (buktiError) {
            console.error('Failed to upload surat lainnya bukti:', buktiError)
            notification.warning({
              message: 'Bukti pengiriman gagal diunggah',
              description: 'Silakan coba lagi atau hubungi administrator',
            })
          }
        }

        // Then mark as sent
        const response = await suratKeluarAPI.markSent(id)

        if (response.data?.status === 'success') {
          notification.success({
            message: 'Surat berhasil ditandai sebagai terkirim',
          })
          // Refresh data to show updated status
          await fetchData()
          // Clear the stored bukti files
          perwakilanBuktiFiles.value = {}
          suratLainnyaBuktiFilesLocal.value = []
        } else {
          notification.error({
            message: response.data?.message || 'Gagal menandai surat sebagai terkirim',
          })
        }
      } catch (error) {
        console.error('Error marking surat as sent:', error)
        notification.error({
          message: error.response?.data?.message || 'Gagal menandai surat sebagai terkirim',
        })
      } finally {
        markSentLoading.value = false
      }
    }

    // Trigger file input click
    const triggerFileInput = perwakilanId => {
      const key = perwakilanId || 'default'
      const inputRef = fileInputRefs.get(key)
      if (inputRef) {
        inputRef.click()
      }
    }

    // Handle file selection
    const handleFileChange = (event, perwakilanId) => {
      const files = Array.from(event.target.files || [])
      processFiles(files, perwakilanId)
      // Clear input so same file can be selected again
      const key = perwakilanId || 'default'
      const inputRef = fileInputRefs.get(key)
      if (inputRef) {
        inputRef.value = ''
      }
    }

    // Handle file drop
    const handleFileDrop = (event, perwakilanId) => {
      const files = Array.from(event.dataTransfer.files || [])
      processFiles(files, perwakilanId)
      event.currentTarget.classList.remove('drag-over')
    }

    // Handle drag over
    const handleDragOver = event => {
      event.currentTarget.classList.add('drag-over')
    }

    // Handle drag leave
    const handleDragLeave = event => {
      event.currentTarget.classList.remove('drag-over')
    }

    // Process files (validate and add to list)
    const processFiles = (files, perwakilanId) => {
      const allowedTypes = ['application/pdf', 'image/png', 'image/jpeg']
      const maxSize = 10 * 1024 * 1024 // 10MB

      if (perwakilanId) {
        // For Surat Tugas: Only allow one file per perwakilan
        if (files.length > 1) {
          notification.warning({ message: 'Hanya dapat mengunggah 1 file per perwakilan' })
          files = [files[0]]
        }

        const file = files[0]
        if (!file) return

        // Validate file type
        if (!allowedTypes.includes(file.type)) {
          notification.error({
            message: `File "${file.name}" memiliki format yang tidak didukung. Gunakan PDF, PNG, atau JPG/JPEG`,
          })
          return
        }

        // Validate file size
        if (file.size > maxSize) {
          notification.error({
            message: `File "${file.name}" melebihi ukuran maksimal 10MB`,
          })
          return
        }

        // Add uid for tracking
        const fileWithUid = Object.assign(file, {
          uid: `file-${Date.now()}-${Math.random()
            .toString(36)
            .substr(2, 9)}`,
        })

        // Store in perwakilan-specific map
        perwakilanFiles.value[perwakilanId] = fileWithUid
      } else {
        // For Surat Lainnya: Allow multiple files
        for (const file of files) {
          // Validate file type
          if (!allowedTypes.includes(file.type)) {
            notification.error({
              message: `File "${file.name}" memiliki format yang tidak didukung. Gunakan PDF, PNG, atau JPG/JPEG`,
            })
            continue
          }

          // Validate file size
          if (file.size > maxSize) {
            notification.error({
              message: `File "${file.name}" melebihi ukuran maksimal 10MB`,
            })
            continue
          }

          // Add uid for tracking
          const fileWithUid = Object.assign(file, {
            uid: `file-${Date.now()}-${Math.random()
              .toString(36)
              .substr(2, 9)}`,
          })

          uploadFileList.value.push(fileWithUid)
        }
      }
    }

    // Get file object URL for preview
    const getFileObjectUrl = file => {
      if (file.originFileObj) {
        return URL.createObjectURL(file.originFileObj)
      }
      if (file instanceof File) {
        return URL.createObjectURL(file)
      }
      return '#'
    }

    // Get file for specific perwakilan
    const getPerwakilanFile = perwakilanId => {
      return perwakilanFiles.value[perwakilanId]
    }

    // Remove file from list
    const removeFile = (perwakilanId, index) => {
      if (perwakilanId) {
        // Remove perwakilan file
        delete perwakilanFiles.value[perwakilanId]
      } else {
        // Remove from Surat Lainnya list
        uploadFileList.value.splice(index, 1)
      }
    }

    // Handle Surat Lainnya bukti file change in archive section
    const handleSuratLainnyaBuktiFileChange = event => {
      const files = Array.from(event.target.files || [])
      const allowedTypes = ['application/pdf', 'image/png', 'image/jpeg']
      const maxSize = 10 * 1024 * 1024 // 10MB

      files.forEach(file => {
        // Validate file type
        if (!allowedTypes.includes(file.type)) {
          notification.error({
            message: 'Format file tidak didukung',
            description: `File ${file.name} harus berformat PDF, PNG, atau JPG/JPEG`,
          })
          return
        }

        // Validate file size
        if (file.size > maxSize) {
          notification.error({
            message: 'Ukuran file terlalu besar',
            description: `File ${file.name} melebihi batas maksimal 10MB`,
          })
          return
        }

        suratLainnyaBuktiFiles.value.push(file)
      })

      // Clear input
      if (event.target) {
        event.target.value = ''
      }
    }

    // Remove Surat Lainnya bukti file from archive section
    const removeSuratLainnyaBuktiFile = index => {
      suratLainnyaBuktiFiles.value.splice(index, 1)
    }

    // Confirm and submit archive
    const confirmSubmitArchive = async () => {
      arsipModalVisible.value = false
      const id = route.params.id
      archiveLoading.value = true

      try {
        if (isSuratTugas.value) {
          // For Surat Tugas: Upload files for each perwakilan
          const perwakilanList = suratData.value.perwakilanDinas || []
          const filesToUpload = []

          // Collect all files to upload
          for (const perwakilan of perwakilanList) {
            const file = perwakilanFiles.value[perwakilan.id]
            if (file) {
              filesToUpload.push({ perwakilan, file })
            }
          }

          if (filesToUpload.length === 0) {
            notification.error({ message: 'Silakan unggah minimal satu dokumen surat tugas' })
            archiveLoading.value = false
            return
          }

          // Upload each file, marking the last one as final
          for (let i = 0; i < filesToUpload.length; i++) {
            const { perwakilan, file } = filesToUpload[i]
            const isLastUpload = i === filesToUpload.length - 1
            const perwakilanName = perwakilan.isNonPengurus
              ? perwakilan.namaNonPengurus
              : perwakilan.user?.name

            const formData = new FormData()
            const actualFile = file.originFileObj || file

            formData.append('file', actualFile)
            // Use per-perwakilan status (pengurus auto-set to 'Sudah dikirim')
            formData.append(
              'statusPengiriman',
              perwakilanStatusPengiriman.value[perwakilan.id] || 'Sudah dikirim',
            )
            formData.append('perwakilanDinasId', perwakilan.id)
            formData.append('isFinalUpload', isLastUpload ? 'true' : 'false')

            const response = await suratKeluarAPI.archiveHardCopy(id, formData)

            if (response.data?.status !== 'success') {
              notification.error({
                message: `Gagal mengunggah file untuk ${perwakilanName}`,
              })
              archiveLoading.value = false
              return
            }
          }

          notification.success({
            message: `Berhasil mengunggah ${filesToUpload.length} file surat tugas`,
          })

          // After all signed PDFs uploaded, upload bukti pengiriman for non-pengurus
          const perwakilanWithBukti = Object.keys(perwakilanBuktiFiles.value)
          if (perwakilanWithBukti.length > 0) {
            for (const perwakilanId of perwakilanWithBukti) {
              const buktiFiles = perwakilanBuktiFiles.value[perwakilanId]
              if (buktiFiles && buktiFiles.length > 0) {
                try {
                  const buktiFormData = new FormData()
                  // Backend now accepts multiple files with field name 'bukti_pengiriman'
                  buktiFiles.forEach(file => {
                    buktiFormData.append('bukti_pengiriman', file.originFileObj || file)
                  })

                  const buktiResponse = await suratKeluarAPI.uploadBuktiPengiriman(
                    id,
                    perwakilanId,
                    buktiFormData,
                  )

                  if (buktiResponse.data.status !== 'success') {
                    throw new Error('Upload failed')
                  }
                } catch (buktiError) {
                  console.error(`Error uploading bukti for perwakilan ${perwakilanId}:`, buktiError)
                  // Don't block the flow, just log the error
                  notification.warning({
                    message: 'Beberapa bukti pengiriman gagal diunggah',
                    description:
                      'Arsip surat berhasil disimpan, namun ada bukti pengiriman yang gagal',
                  })
                }
              }
            }
          }
        } else {
          // For Surat Lainnya: Upload all files in uploadFileList
          if (!uploadFileList.value.length) {
            notification.error({ message: 'Silakan unggah dokumen surat terlebih dahulu' })
            archiveLoading.value = false
            return
          }

          // Determine the status to send to archive API
          // If user wants "Sudah dikirim" AND has bukti files, archive as "Belum dikirim" first
          // Then upload bukti which will change status to "Terkirim"
          const hasBuktiFiles = suratLainnyaBuktiFiles.value.length > 0
          const wantsSudahDikirim = statusPengiriman.value === 'Sudah dikirim'
          const archiveStatus =
            wantsSudahDikirim && hasBuktiFiles ? 'Belum dikirim' : statusPengiriman.value

          for (let i = 0; i < uploadFileList.value.length; i++) {
            const file = uploadFileList.value[i]
            const isLastUpload = i === uploadFileList.value.length - 1

            const formData = new FormData()
            const actualFile = file.originFileObj || file
            formData.append('file', actualFile)
            formData.append('statusPengiriman', archiveStatus)
            formData.append('isFinalUpload', isLastUpload ? 'true' : 'false')

            const response = await suratKeluarAPI.archiveHardCopy(id, formData)

            if (response.data?.status !== 'success') {
              notification.error({
                message: response.data?.message || `Gagal mengunggah file ${file.name}`,
              })
              archiveLoading.value = false
              return
            }
          }

          notification.success({
            message: `Berhasil mengunggah ${uploadFileList.value.length} file arsip surat`,
          })

          // Upload bukti pengiriman for Surat Lainnya (if provided)
          // This will automatically change status from "Belum Dikirim" to "Terkirim"
          if (hasBuktiFiles) {
            try {
              const buktiFormData = new FormData()
              suratLainnyaBuktiFiles.value.forEach(file => {
                buktiFormData.append('bukti_pengiriman', file)
              })

              const buktiResponse = await suratKeluarAPI.uploadBuktiPengirimanSurat(
                id,
                buktiFormData,
              )

              if (buktiResponse.data.status === 'success') {
                notification.success({
                  message: 'Bukti pengiriman berhasil diunggah',
                  description: 'Status surat diubah menjadi Terkirim',
                })
              }
            } catch (buktiError) {
              console.error('Error uploading bukti for Surat Lainnya:', buktiError)
              // Don't block the flow, just log the error
              notification.warning({
                message: 'Arsip berhasil disimpan, namun bukti pengiriman gagal diunggah',
                description: 'Status surat masih "Belum Dikirim". Anda dapat upload bukti nanti.',
              })
            }
          }
        }

        // Redirect to detail hardcopy page
        window.location.reload()
      } catch (error) {
        console.error('Error archiving surat:', error)
        notification.error({
          message: error.response?.data?.message || 'Gagal menyimpan arsip surat',
        })
      } finally {
        archiveLoading.value = false
      }
    }

    // Open surat masuk preview
    const openSuratMasukPreview = () => {
      if (suratData.value.suratUndanganAcara?.pathToFile) {
        currentPdfPath.value = suratData.value.suratUndanganAcara.pathToFile
        previewTitle.value =
          getSuratMasukFileName(suratData.value.suratUndanganAcara.pathToFile) || 'Surat Masuk'
        suratMasukPreviewVisible.value = true
      }
    }

    // Open surat masuk in new tab
    const openSuratMasukNewTab = () => {
      if (suratData.value.suratUndanganAcara?.pathToFile) {
        window.open(getFileUrl(suratData.value.suratUndanganAcara.pathToFile), '_blank')
      }
    }

    // Open Surat Tugas preview with PDFPreviewModal
    const openSuratTugasPreview = () => {
      if (suratTugasList.value.length === 0) {
        notification.warning({ message: 'Tidak ada dokumen surat tugas' })
        return
      }

      // For multiple files, PDFPreviewModal will handle navigation
      currentSuratTugasIndex.value = 0
      previewTitle.value = 'Preview Surat Keluar'
      currentPdfPath.value = '' // PDFPreviewModal will use pdf-list instead
      suratMasukPreviewVisible.value = true
    }

    // Open Surat Tugas in new tab (handles multiple files)
    const openSuratTugasNewTab = () => {
      if (suratTugasList.value.length === 0) {
        notification.warning({ message: 'Tidak ada dokumen surat tugas' })
        return
      }

      // Open each file in a new tab with slight delay to prevent popup blocker
      suratTugasList.value.forEach((st, index) => {
        setTimeout(() => {
          if (st.pdfPath) {
            window.open(getFileUrl(st.pdfPath), '_blank')
          }
        }, index * 100) // 100ms delay between each tab
      })

      if (suratTugasList.value.length > 1) {
        notification.success({
          message: `Membuka ${suratTugasList.value.length} dokumen surat tugas`,
          description: 'Pastikan popup tidak diblokir oleh browser.',
        })
      }
    }

    // Open Surat Lainnya preview with PDFPreviewModal
    const openSuratLainnyaPreview = () => {
      if (!suratLainnyaDocumentPath.value) {
        notification.warning({ message: 'Dokumen surat tidak tersedia' })
        return
      }

      previewTitle.value = `Preview Surat ${suratData.value.nomorSurat}`
      currentPdfPath.value = suratLainnyaDocumentPath.value
      currentSuratTugasIndex.value = 0
      suratMasukPreviewVisible.value = true
    }

    // Open Surat Lainnya in new tab
    const openSuratLainnyaNewTab = () => {
      if (!suratLainnyaDocumentPath.value) {
        notification.warning({ message: 'Dokumen surat tidak tersedia' })
        return
      }

      window.open(getFileUrl(suratLainnyaDocumentPath.value), '_blank')
    }

    // Open Bukti Pengiriman preview with PDFPreviewModal
    const openBuktiPreview = (buktiPath) => {
      if (!buktiPath) {
        notification.warning({ message: 'Bukti pengiriman tidak tersedia' })
        return
      }

      previewTitle.value = 'Preview Bukti Pengiriman'
      currentPdfPath.value = buktiPath
      currentSuratTugasIndex.value = 0
      suratMasukPreviewVisible.value = true
    }

    // Open Bukti Pengiriman preview for perwakilan (from JSON array)
    const openBuktiPreviewFromList = (buktiPengirimanJson, index = 0) => {
      if (!buktiPengirimanJson) {
        notification.warning({ message: 'Bukti pengiriman tidak tersedia' })
        return
      }

      try {
        const files = JSON.parse(buktiPengirimanJson)
        if (Array.isArray(files) && files.length > 0) {
          const pdfList = files.map(path => getFileUrl(path))
          pdfListForModal.value = pdfList
          currentSuratTugasIndex.value = Math.min(index, pdfList.length - 1)
          previewTitle.value = 'Preview Bukti Pengiriman'
          currentPdfPath.value = '' // Use pdf-list instead
          suratMasukPreviewVisible.value = true
        }
      } catch (e) {
        // Old format (single file)
        previewTitle.value = 'Preview Bukti Pengiriman'
        currentPdfPath.value = buktiPengirimanJson
        currentSuratTugasIndex.value = 0
        suratMasukPreviewVisible.value = true
      }
    }

    // Get surat masuk file name from path
    const getSuratMasukFileName = pathToFile => {
      if (!pathToFile) return ''
      const parts = pathToFile.split('/')
      const fileNameWithPrefix = parts[parts.length - 1]
      // Remove number prefix like "2_" from filename
      const withoutPrefix = fileNameWithPrefix.replace(/^\d+_/, '')
      return withoutPrefix
    }

    // Get file name from path (generic)
    const getFileNameFromPath = pathToFile => {
      if (!pathToFile) return 'Bukti Pengiriman'
      const parts = pathToFile.split('/')
      return parts[parts.length - 1] || 'Bukti Pengiriman'
    }

    // Check if file is an image
    const isImageFile = fileName => {
      if (!fileName) return false
      const ext = fileName
        .toLowerCase()
        .split('.')
        .pop()
      return ['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(ext)
    }

    // Open Bukti Pengiriman Modal for non-pengurus
    const openBuktiPengirimanModal = perwakilanId => {
      currentNonPengurusId.value = perwakilanId

      // Load existing bukti files for this perwakilan from state
      buktiPengirimanFiles.value = perwakilanBuktiFiles.value[perwakilanId] || []

      // If no files in state, check if there's already uploaded bukti from server
      if (buktiPengirimanFiles.value.length === 0) {
        const perwakilan = suratData.value.perwakilanDinas?.find(p => p.id === perwakilanId)
        if (perwakilan?.buktiPengiriman) {
          try {
            // Parse JSON array of file paths
            const filePaths = JSON.parse(perwakilan.buktiPengiriman)
            // Create file object representations for existing bukti files
            buktiPengirimanFiles.value = filePaths.map((path, index) => ({
              uid: `existing-bukti-${index}`,
              name: getFileNameFromPath(path),
              url: getFileUrl(path),
              isExisting: true, // Flag to identify server files
            }))
          } catch (e) {
            // If not JSON (old single file format), treat as single file
            buktiPengirimanFiles.value = [
              {
                uid: 'existing-bukti',
                name: getFileNameFromPath(perwakilan.buktiPengiriman),
                url: getFileUrl(perwakilan.buktiPengiriman),
                isExisting: true,
              },
            ]
          }
        }
      }

      buktiPengirimanModalVisible.value = true
    }

    const closeBuktiPengirimanModal = () => {
      buktiPengirimanModalVisible.value = false
      buktiPengirimanFiles.value = []
      currentNonPengurusId.value = null
    }

    // Surat Lainnya bukti functions (surat-level)
    const openSuratLainnyaBuktiModal = () => {
      suratLainnyaBuktiFiles.value = []
      suratLainnyaBuktiModalVisible.value = true
    }

    const handleSuratLainnyaBuktiChange = info => {
      suratLainnyaBuktiFiles.value = info.fileList
    }

    const handleSuratLainnyaBuktiRemove = file => {
      const index = suratLainnyaBuktiFiles.value.indexOf(file)
      if (index > -1) {
        suratLainnyaBuktiFiles.value.splice(index, 1)
      }
    }

    const handleSubmitSuratLainnyaBukti = async () => {
      // Validate that files are uploaded
      if (suratLainnyaBuktiFiles.value.length === 0) {
        notification.warning({
          message: 'Bukti pengiriman belum diunggah',
          description: 'Mohon upload minimal 1 file bukti pengiriman',
        })
        return
      }

      suratLainnyaBuktiUploading.value = true
      try {
        const id = route.params.id
        const formData = new FormData()

        // Append all files
        suratLainnyaBuktiFiles.value.forEach(fileObj => {
          formData.append('bukti_pengiriman', fileObj.originFileObj)
        })

        // Call surat-level API
        const response = await suratKeluarAPI.uploadBuktiPengirimanSurat(id, formData)

        if (response.data?.status === 'success') {
          suratLainnyaBuktiModalVisible.value = false
          notification.success({
            message: 'Bukti pengiriman berhasil diunggah',
            description: 'Surat telah ditandai sebagai terkirim',
          })
          await fetchData() // Refresh to show new status and bukti
        }
      } catch (error) {
        console.error('Error uploading surat lainnya bukti:', error)
        notification.error({
          message: 'Gagal mengunggah bukti pengiriman',
          description: error.response?.data?.message || 'Terjadi kesalahan',
        })
      } finally {
        suratLainnyaBuktiUploading.value = false
      }
    }

    // Trigger file input for bukti pengiriman
    const triggerBuktiFileInput = () => {
      if (buktiFileInputRef.value) {
        buktiFileInputRef.value.click()
      }
    }

    // Handle bukti pengiriman file selection
    const handleBuktiFileChange = event => {
      const files = Array.from(event.target.files || [])
      processBuktiFiles(files)
      // Clear input so same file can be selected again
      if (buktiFileInputRef.value) {
        buktiFileInputRef.value.value = ''
      }
    }

    // Process bukti pengiriman files
    const processBuktiFiles = files => {
      const allowedTypes = ['application/pdf', 'image/png', 'image/jpeg']
      const maxSize = 10 * 1024 * 1024 // 10MB

      files.forEach(file => {
        // Validate file type
        if (!allowedTypes.includes(file.type)) {
          notification.error({
            message: 'Format file tidak didukung',
            description: `File ${file.name} harus berformat PDF, PNG, atau JPG/JPEG`,
          })
          return
        }

        // Validate file size
        if (file.size > maxSize) {
          notification.error({
            message: 'Ukuran file terlalu besar',
            description: `File ${file.name} melebihi batas maksimal 10MB`,
          })
          return
        }

        // Add unique identifier
        const fileWithUid = Object.assign(file, {
          uid: `bukti-${Date.now()}-${Math.random()
            .toString(36)
            .substr(2, 9)}`,
        })

        buktiPengirimanFiles.value.push(fileWithUid)
      })
    }

    // Handle edit bukti file (replace existing file)
    const handleEditBuktiFile = index => {
      // Create a temporary file input to select replacement
      const tempInput = document.createElement('input')
      tempInput.type = 'file'
      tempInput.accept = '.pdf,.png,.jpg,.jpeg'
      tempInput.onchange = e => {
        const files = Array.from(e.target.files || [])
        if (files.length > 0) {
          const allowedTypes = ['application/pdf', 'image/png', 'image/jpeg']
          const maxSize = 10 * 1024 * 1024

          const file = files[0]
          if (!allowedTypes.includes(file.type)) {
            notification.error({
              message: 'Format file tidak didukung',
              description: 'File harus berformat PDF, PNG, atau JPG/JPEG',
            })
            return
          }

          if (file.size > maxSize) {
            notification.error({
              message: 'Ukuran file terlalu besar',
              description: 'File melebihi batas maksimal 10MB',
            })
            return
          }

          const fileWithUid = Object.assign(file, {
            uid: `bukti-${Date.now()}-${Math.random()
              .toString(36)
              .substr(2, 9)}`,
          })

          buktiPengirimanFiles.value[index] = fileWithUid
          notification.success({ message: 'File berhasil diganti' })
        }
      }
      tempInput.click()
    }

    // Remove bukti file
    const removeBuktiFile = async index => {
      const file = buktiPengirimanFiles.value[index]

      // If it's an existing server file, ask for confirmation and delete from server
      if (file.isExisting && isArchivedStatus.value) {
        Modal.confirm({
          title: 'Hapus Bukti Pengiriman?',
          content: 'Bukti pengiriman yang sudah diunggah akan dihapus dari server. Lanjutkan?',
          okText: 'Ya, Hapus',
          okType: 'danger',
          cancelText: 'Batal',
          onOk: async () => {
            try {
              const id = route.params.id
              await suratKeluarAPI.deleteBuktiPengiriman(id, currentNonPengurusId.value)

              notification.success({
                message: 'Bukti pengiriman berhasil dihapus',
              })

              buktiPengirimanFiles.value.splice(index, 1)
              await fetchData() // Refresh data
            } catch (error) {
              notification.error({
                message: 'Gagal menghapus bukti pengiriman',
                description: error.response?.data?.message,
              })
            }
          },
        })
      } else {
        // For new files (not yet uploaded), just remove from array
        buktiPengirimanFiles.value.splice(index, 1)
      }
    }

    // Submit bukti pengiriman
    // For archived statuses: upload immediately
    // For Penandatanganan status: save to state (will be uploaded with archive submission)
    const handleSubmitBuktiPengiriman = async () => {
      if (buktiPengirimanFiles.value.length === 0) {
        notification.warning({ message: 'Mohon upload minimal 1 bukti pengiriman' })
        return
      }

      if (!currentNonPengurusId.value) {
        notification.error({ message: 'Perwakilan tidak ditemukan' })
        return
      }

      // Check if status is Terkirim (already sent) - only this should upload immediately
      if (suratData.value.status === 'Terkirim') {
        // Upload immediately for Terkirim status only
        buktiPengirimanLoading.value = true

        try {
          const id = route.params.id
          const buktiFormData = new FormData()

          // Backend now accepts multiple files with field name 'bukti_pengiriman'
          buktiPengirimanFiles.value.forEach(file => {
            buktiFormData.append('bukti_pengiriman', file.originFileObj || file)
          })

          // Call API to upload bukti pengiriman
          const buktiResponse = await suratKeluarAPI.uploadBuktiPengiriman(
            id,
            currentNonPengurusId.value,
            buktiFormData,
          )

          if (buktiResponse.data.status === 'success') {
            notification.success({
              message: 'Bukti Pengiriman Berhasil Diunggah',
              description: 'Dokumen bukti pengiriman telah tersimpan',
            })

            // Close modal and refresh data
            closeBuktiPengirimanModal()
            await fetchData() // Refresh to show uploaded bukti
          }
        } catch (error) {
          console.error('Upload bukti error:', error)
          notification.error({
            message: 'Gagal mengunggah bukti pengiriman',
            description: error.response?.data?.message || 'Terjadi kesalahan saat mengunggah',
          })
        } finally {
          buktiPengirimanLoading.value = false
        }
      } else {
        // For Penandatanganan and Belum Dikirim status: save to state, will be uploaded later
        perwakilanBuktiFiles.value[currentNonPengurusId.value] = [...buktiPengirimanFiles.value]

        notification.success({
          message: 'Bukti Pengiriman Tersimpan',
          description:
            suratData.value.status === 'Belum Dikirim'
              ? 'Bukti akan diunggah saat surat ditandai sebagai terkirim'
              : 'Bukti akan diunggah saat arsip disimpan',
        })

        // Close modal
        closeBuktiPengirimanModal()
      }
    }

    // Helper function to get bukti file count from JSON string
    const getBuktiFileCount = buktiPengirimanJson => {
      if (!buktiPengirimanJson) return 0
      try {
        const files = JSON.parse(buktiPengirimanJson)
        return Array.isArray(files) ? files.length : 1
      } catch (e) {
        return 1 // Old format (single file)
      }
    }

    // Helper function to get array of file URLs from JSON string
    const getBuktiFileUrls = buktiPengirimanJson => {
      if (!buktiPengirimanJson) return []
      try {
        const files = JSON.parse(buktiPengirimanJson)
        if (Array.isArray(files)) {
          return files.map(path => getFileUrl(path))
        }
        return [getFileUrl(buktiPengirimanJson)]
      } catch (e) {
        return [getFileUrl(buktiPengirimanJson)] // Old format (single file)
      }
    }

    onMounted(() => {
      fetchData()
    })

    return {
      pageLoading,
      cancelLoading,
      archiveLoading,
      markSentLoading,
      suratData,
      suratTugasList,
      isSuratTugas,
      isArchivedStatus,
      hasDokumenSurat,
      suratLainnyaDocumentPath,
      nonPengurusPerwakilanList,
      hasNonPengurusWithBuktiAccess,
      hasSuratLainnyaBukti,
      suratLainnyaBuktiList,
      getSuratSectionTitle,
      getStatusBadgeClass,
      hasPdf,
      canEdit,
      canPerformActions,
      canSubmitArchive,
      canMarkSent,
      canDuplicate,
      isUploadFileMethod,
      cancelModalVisible,
      cancelReason,
      suratMasukPreviewVisible,
      suratMasukPreviewUrl,
      perwakilanFiles,
      uploadFileList,
      statusPengiriman,
      perwakilanStatusPengiriman,
      perwakilanBuktiFiles,
      isOwner,
      canCancel,
      getStatusStyle,
      formatCreator,
      formatCreatedAt,
      getBentukSuratDescription,
      formatPenandatangan,
      formatVerifikatorList,
      getFileUrl,
      handleDownloadSurat,
      handleEditSurat,
      handleCancelSurat,
      confirmCancelSurat,
      arsipModalVisible,
      confirmSubmitArchive,
      handleDuplicateSurat,
      openTandaiTerkirimModal,
      handleMarkAsSent,
      setFileInputRef,
      triggerFileInput,
      handleFileChange,
      handleFileDrop,
      handleDragOver,
      handleDragLeave,
      getFileObjectUrl,
      getPerwakilanFile,
      removeFile,
      openSuratMasukPreview,
      openSuratMasukNewTab,
      openSuratTugasPreview,
      openSuratTugasNewTab,
      openSuratLainnyaPreview,
      openSuratLainnyaNewTab,
      previewTitle,
      currentPdfPath,
      currentSuratTugasIndex,
      pdfListForModal,
      getSuratMasukFileName,
      getFileNameFromPath,
      isImageFile,
      getBuktiFileCount,
      getBuktiFileUrls,
      // Bukti Pengiriman
      buktiPengirimanModalVisible,
      buktiPengirimanFiles,
      buktiPengirimanLoading,
      buktiFileInputRef,
      currentNonPengurusId,
      getBuktiFileList,
      openBuktiPengirimanModal,
      closeBuktiPengirimanModal,
      triggerBuktiFileInput,
      handleBuktiFileChange,
      processBuktiFiles,
      handleEditBuktiFile,
      removeBuktiFile,
      handleSubmitBuktiPengiriman,
      // Surat Lainnya Bukti
      suratLainnyaBuktiModalVisible,
      suratLainnyaBuktiUploading,
      suratLainnyaBuktiFiles,
      suratLainnyaBuktiInputRef,
      openSuratLainnyaBuktiModal,
      handleSuratLainnyaBuktiChange,
      handleSuratLainnyaBuktiRemove,
      handleSubmitSuratLainnyaBukti,
      handleSuratLainnyaBuktiFileChange,
      removeSuratLainnyaBuktiFile,
      // Bukti preview
      openBuktiPreview,
      openBuktiPreviewFromList,
      // Inline bukti upload
      suratLainnyaBuktiFilesLocal,
      suratLainnyaBuktiInputInline,
      setBuktiFileInputRef,
      triggerPerwakilanBuktiInput,
      handlePerwakilanBuktiFileChange,
      triggerSuratLainnyaBuktiInput,
      handleSuratLainnyaBuktiInlineChange,
      window,
    }
  },
})
</script>

<style lang="scss" scoped>
/* =========================================
   1. GLOBAL UTILITIES & LAYOUT
   ========================================= */
.surat-keluar-detail-hc {
  padding: 24px;
  background: #fcfcfc;
  min-height: calc(100vh - 64px);
  position: relative;

  @media (max-width: 768px) {
    padding: 16px;
  }
}

.loading-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(255, 255, 255, 0.8);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 100;
}

.confirm-modal {
  .modal-description {
    color: #595959;
    font-size: 14px;
    line-height: 1.6;
    margin-bottom: 32px;
  }

  .modal-buttons {
    display: flex;
    gap: 16px;
    width: 100%;

    /* Force buttons to share width equally (50:50) */
    .btn-pill-black,
    .btn-pill-red {
      flex: 1; /* Grow to fill space */
      width: 100%; /* Ensure base width is full to allow flex to split */
      text-align: center;
      justify-content: center; /* Center text/icon flex content */
    }
  }
}

/* =========================================
   2. BUTTON STYLES (Source of Truth)
   ========================================= */

/* A. SOLID RED PILL (For: Save, Download, Mark Sent) */
.btn-pill-red {
  border-radius: 50px;
  font-size: 14px;
  font-weight: 700;
  border: 2px solid #e10f28;
  background: #e10f28;
  color: white;
  padding: 6px 20px;
  min-height: auto;
  height: 42px;
  line-height: 1.5;
  transition: all 0.3s;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  box-shadow: none;

  /* Force Icon Color White */
  :deep(.anticon) {
    color: white;
  }

  &:hover:not(:disabled) {
    background: white !important; /* Darker Red */
    border-color: #e10f28 !important;
    color: #e10f28 !important;
  }

  &:disabled {
    border-color: #d9d9d9;
    background: #f5f5f5;
    color: #d9d9d9;
    cursor: not-allowed;
  }
}

/* B. OUTLINE RED PILL (For: Tambah Foto, Upload) */
.btn-pill-red-outline {
  border-radius: 50px;
  font-size: 14px;
  font-weight: 700;
  border: 2px solid #e10f28;
  background: white;
  color: #e10f28;
  padding: 6px 20px;
  min-height: auto;
  height: 42px;
  line-height: 1.5;
  transition: all 0.3s;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  box-shadow: none;

  /* Force Icon Color Red */
  :deep(.anticon) {
    color: #e10f28;
  }

  &:hover:not(:disabled) {
    background: #e10f28 !important; /* Light Pink */
    border-color: #e10f28 !important;
    color: white !important;

    :deep(.anticon) {
      color: white;
    }
  }

  &:disabled {
    border-color: #d9d9d9;
    background: #f5f5f5;
    color: #d9d9d9;
  }
}

/* C. BLACK OUTLINE PILL (For: Cancel, Back, Edit) */
.btn-pill-black {
  border-radius: 50px;
  font-size: 14px;
  font-weight: 700;
  border: 2px solid #262626;
  background: white;
  color: #262626;
  padding: 6px 20px;
  min-height: auto;
  height: 42px;
  line-height: 1.5;
  transition: all 0.3s;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  box-shadow: none;

  &:hover:not(:disabled) {
    background: #262626 !important;
    border-color: #262626 !important;
    color: white !important;
  }

  &:disabled {
    border-color: #d9d9d9;
    background: #f5f5f5;
    color: #d9d9d9;
  }
}

/* =========================================
   3. PAGE HEADER & ACTIONS
   ========================================= */
.page-header {
  margin-bottom: 24px;

  .page-title {
    font-size: 24px;
    font-weight: 600;
    color: #262626;
    margin: 0 0 4px 0;
  }

  .page-subtitle {
    font-size: 16px;
    color: #595959;
    margin: 0 0 8px 0;
  }

  .status-tag {
    border-radius: 4px;
    padding: 2px 8px;
    font-size: 12px;
    font-weight: 500;
  }
}

.action-buttons {
  display: flex;
  gap: 12px;
  margin-bottom: 24px;
  flex-wrap: wrap;

  /* Buttons here will auto-use the classes defined above */
  .btn-pill-red,
  .btn-pill-black {
    @media (max-width: 768px) {
      width: 100%;
    }
  }
}

/* =========================================
   4. INFO CARD
   ========================================= */
.info-card {
  background: white;
  border-radius: 8px;
  padding: 24px;
  margin-bottom: 24px;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.06);

  .section-title {
    font-size: 18px;
    font-weight: 600;
    color: #262626;
    margin: 0 0 24px 0;
  }

  .info-row {
    display: flex;
    padding: 12px 0;
    border-bottom: 1px solid #f0f0f0;
    align-items: center;

    &:last-child {
      border-bottom: none;
    }

    .info-label {
      width: 200px;
      color: #8c8c8c;
      font-weight: 500;
      flex-shrink: 0;
    }

    .info-value {
      color: #262626;
      flex: 1;
    }
  }
}

/* Surat Masuk / Bukti Links inside Info Card */
.surat-masuk-container {
  display: flex;
  align-items: center;
  flex: 1;
  gap: 8px;
  min-width: 0;

  .surat-masuk-icon {
    color: #8c8c8c;
    font-size: 16px;
    flex-shrink: 0;
  }

  .surat-masuk-name,
  .file-link {
    color: #321e8d;
    font-size: 14px;
    text-decoration: none;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    flex: 1;
    display: block;

    &:hover {
      text-decoration: underline;
    }
  }

  .surat-masuk-actions {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-left: 4px;
    flex-shrink: 0;

    .action-icon {
      display: flex;
      align-items: center;
      justify-content: center;
      width: 24px;
      height: 24px;
      color: #e01f48;
      cursor: pointer;
      border-radius: 4px;

      &:hover {
        background-color: #fff1f0;
      }
    }
  }
}

/* =========================================
   5. ARCHIVE & BUKTI UPLOAD SECTIONS
   ========================================= */
.archive-section {
  background: white;
  border-radius: 8px;
  padding: 24px;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.06);

  .section-title {
    font-size: 16px;
    font-weight: 600;
    margin-bottom: 8px;
  }

  .section-description {
    font-size: 14px;
    color: #8c8c8c;
    margin-bottom: 24px;
  }

  .perwakilan-upload-card {
    border: 1px solid #e8e8e8;
    border-radius: 8px;
    padding: 20px;
    margin-bottom: 16px;
    background: white;

    .perwakilan-title {
      font-size: 15px;
      font-weight: 600;
      margin-bottom: 16px;
      padding-bottom: 12px;
      border-bottom: 1px solid #e8e8e8;
    }
  }

  .already-uploaded-notice {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 12px 16px;
    background: #f6ffed;
    border: 1px solid #b7eb8f;
    border-radius: 6px;
    margin-bottom: 12px;
    font-size: 14px;
    color: #52c41a;

    .view-link {
      margin-left: auto;
      color: #1890ff;
    }
  }

  /* Upload Area */
  .upload-area {
    border: 2px dashed #d9d9d9;
    border-radius: 8px;
    background: #fafafa;
    padding: 48px 24px;
    text-align: center;
    cursor: pointer;
    transition: all 0.3s;

    &:hover {
      border-color: #e01f48;
      background: #fff5f7;
    }

    .upload-text {
      font-size: 16px;
      font-weight: 500;
      margin-bottom: 8px;
    }
    .upload-hint {
      font-size: 14px;
      color: #8c8c8c;
    }
  }

  /* File Lists */
  .uploaded-files-list {
    margin-top: 16px;

    .uploaded-file-item {
      display: flex;
      align-items: center;
      padding: 12px 16px;
      background: #fafafa;
      border-radius: 6px;
      margin-bottom: 8px;

      .file-icon {
        color: #e01f48;
        font-size: 18px;
        margin-right: 12px;
      }

      .file-name-link {
        flex: 1;
        color: #1890ff;
        font-size: 14px;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
      }

      .btn-remove-file {
        color: #ff4d4f;
      }
    }
  }
}

.status-radio-group {
  display: flex;
  flex-direction: row;
  gap: 24px; /* Space between radio buttons */
  margin-top: 8px; /* Space from label */

  /* Ensure radio items align nicely */
  :deep(.ant-radio-wrapper) {
    display: flex;
    align-items: center;
    margin-right: 0; /* Remove default margin to use gap instead */
    font-size: 14px;
    color: #262626;
  }
}

.bukti-upload-section,
.bukti-action-section {
  margin-top: 24px;
  padding-top: 24px;
  border-top: 1px solid #f0f0f0;

  .field-label {
    display: block;
    font-weight: 600;
    margin-bottom: 12px;
    .required {
      color: #e01f48;
    }
  }

  .bukti-uploaded-notice {
    margin-top: 12px;
    color: #52c41a;
    display: flex;
    align-items: center;
    gap: 8px;
  }
}

.mark-sent-section {
  margin-top: 24px;
  /* Button will naturally use the .btn-pill-red defined at top */
}

/* =========================================
   6. MODAL STYLES
   ========================================= */
.bukti-pengiriman-modal {
  .modal-description {
    color: #666;
    margin-bottom: 24px;
  }

  .bukti-file-item {
    display: flex;
    align-items: center;
    padding: 12px;
    background: #f9f9f9;
    border: 1px solid #e8e8e8;
    border-radius: 8px;
    margin-bottom: 8px;
    gap: 12px;

    .file-preview-thumbnail {
      width: 40px;
      height: 40px;
      background: #eee;
      border-radius: 4px;
      overflow: hidden;
      display: flex;
      align-items: center;
      justify-content: center;

      img {
        width: 100%;
        height: 100%;
        object-fit: cover;
      }
    }

    .file-name {
      flex: 1;
      font-size: 14px;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }
  }

  .modal-buttons {
    display: flex;
    justify-content: flex-end;
    gap: 12px;
    margin-top: 24px;
    padding-top: 16px;
    border-top: 1px solid #f0f0f0;

    /* Split logic */
    &.split-buttons {
      justify-content: space-between;
      .ant-btn {
        flex: 1;
        width: 100%;
        justify-content: center;
      }
    }
  }
}

.cancel-modal {
  .modal-description {
    color: #595959;
    margin-bottom: 24px;
  }
  .modal-buttons {
    display: flex;
    gap: 16px;
    .ant-btn {
      flex: 1;
      justify-content: center;
    }
  }
}
</style>
